import type { FiliereContenuModule, FiliereContenuPedagogique } from '../../../utils/filiereContenuPedagogique';
import LucideSymbolIcon from '../../icons/LucideSymbolIcon';

type Props = {
  value: FiliereContenuPedagogique;
  onChange: (next: FiliereContenuPedagogique) => void;
};

const fieldClass =
  'academia-field w-full rounded-xl border border-outline-variant/25 bg-white px-3.5 py-2.5 text-sm text-slate-900 shadow-sm transition focus:border-primary/40 focus:ring-2 focus:ring-primary/15';

function linesToList(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}

function listToLines(items: string[]): string {
  return items.join('\n');
}

export default function FiliereContenuPedagogiqueEditor({ value, onChange }: Props) {
  const updateModule = (index: number, patch: Partial<FiliereContenuModule>) => {
    const modules = value.modules.map((m, i) => (i === index ? { ...m, ...patch } : m));
    onChange({ ...value, modules });
  };

  const removeModule = (index: number) => {
    onChange({ ...value, modules: value.modules.filter((_, i) => i !== index) });
  };

  const addModule = () => {
    onChange({
      ...value,
      modules: [...value.modules, { title: '', items: [] }],
    });
  };

  return (
    <div className="space-y-5 rounded-2xl border border-outline-variant/20 bg-surface-container-low/40 p-4 sm:p-5">
      <div className="flex gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <LucideSymbolIcon name="menu_book" tone="primary" size={20} />
        </span>
        <div className="min-w-0">
          <h4 className="text-sm font-black text-slate-900">Contenu de la formation</h4>
          <p className="mt-0.5 text-xs leading-relaxed text-on-surface-variant">
            Technologies et modules affichés sur la page publique (programme type onglets / listes).
          </p>
        </div>
      </div>

      <label className="block min-w-0">
        <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-700">
          Intro programme
        </span>
        <textarea
          className={`${fieldClass} min-h-[72px] resize-y leading-relaxed`}
          value={value.programmeIntro ?? ''}
          onChange={(e) => onChange({ ...value, programmeIntro: e.target.value })}
          rows={3}
          placeholder="Courte présentation du parcours…"
        />
      </label>

      <label className="block min-w-0">
        <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-700">
          Libellé technologies
        </span>
        <input
          className={fieldClass}
          value={value.technologiesLabel ?? ''}
          onChange={(e) => onChange({ ...value, technologiesLabel: e.target.value })}
          placeholder="Technologies abordées"
        />
      </label>

      <label className="block min-w-0">
        <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-700">
          Technologies / compétences
        </span>
        <span className="mb-1.5 block text-[11px] text-on-surface-variant">
          Une entrée par ligne (ex. React, Node.js &amp; Express).
        </span>
        <textarea
          className={`${fieldClass} min-h-[120px] resize-y font-mono text-[13px] leading-relaxed`}
          value={listToLines(value.technologies)}
          onChange={(e) => onChange({ ...value, technologies: linesToList(e.target.value) })}
          rows={5}
          placeholder={'HTML CSS & JavaScript\nReact\nNode.js & Express'}
        />
      </label>

      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs font-bold uppercase tracking-wide text-slate-700">Modules du programme</p>
          <button
            type="button"
            onClick={addModule}
            className="inline-flex items-center gap-1.5 rounded-lg border border-outline-variant/30 bg-white px-3 py-1.5 text-xs font-bold text-slate-800 shadow-sm hover:bg-slate-50"
          >
            <LucideSymbolIcon name="add" tone="inherit" size={16} />
            Ajouter un module
          </button>
        </div>

        {value.modules.length === 0 ? (
          <p className="rounded-xl border border-dashed border-outline-variant/30 bg-white/70 px-4 py-6 text-center text-sm text-on-surface-variant">
            Aucun module. Ajoutez par ex. « Développement front-end » avec une liste de sujets.
          </p>
        ) : (
          value.modules.map((module, index) => (
            <div
              key={`module-${index}`}
              className="rounded-xl border border-outline-variant/20 bg-white p-4 shadow-sm"
            >
              <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                <label className="min-w-0 flex-1">
                  <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-slate-500">
                    Titre du module {index + 1}
                  </span>
                  <input
                    className={fieldClass}
                    value={module.title}
                    onChange={(e) => updateModule(index, { title: e.target.value })}
                    placeholder="Développement front-end"
                  />
                </label>
                <button
                  type="button"
                  onClick={() => removeModule(index)}
                  className="mt-5 inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50"
                  aria-label={`Supprimer le module ${index + 1}`}
                >
                  <LucideSymbolIcon name="delete" tone="inherit" size={16} />
                  Retirer
                </button>
              </div>
              <label className="block min-w-0">
                <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-slate-500">
                  Contenu (une ligne = un point)
                </span>
                <textarea
                  className={`${fieldClass} min-h-[100px] resize-y leading-relaxed`}
                  value={listToLines(module.items)}
                  onChange={(e) => updateModule(index, { items: linesToList(e.target.value) })}
                  rows={4}
                  placeholder={
                    'Création des interfaces web responsives\nIntroduction aux SPA\nInterfaces riches avec React'
                  }
                />
              </label>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
